import { OptimizedImage } from "@/components/OptimizedImage";
import { ServiceBookingOptions } from "@/components/ServiceBookingOptions";
import { useLocation } from "react-router-dom";
import { Link } from "@/i18n/LocalizedLink";
import { useLanguage } from "@/context/LanguageContext";
import { Seo } from "@/components/Seo";
import { Eyebrow, QuoteButton, FaqAccordion, Breadcrumbs } from "@/components/shared";
import { CallButton, WhatsAppButton } from "@/components/ActionButtons";
import { getLocationBySlug } from "@/config/locations";
import { SITE, buildTripMessage } from "@/config/site";
import { VehicleOptions } from "@/components/VehicleOptions";
import { VehicleTariffTable } from "@/components/VehicleTariffTable";
import { FareTerms } from "@/components/FareTerms";
import { getVehicleGuidance } from "@/config/locationVehicleGuidance";
import { buildLocationSchemas } from "@/config/structuredData";
import { getLocalBookingFaqsHi } from "@/config/locationSearchContentHi";
import { canonicalPath } from "@/lib/canonicalPath";
import NotFound from "@/pages/NotFound";
import { MapPin, Check, Car, Info, ArrowRight } from "lucide-react";

export default function LocationPage() {
  const { locale, t, tParts, getLocalizedLocation, basePath } = useLanguage();
  const location = useLocation();
  const loc = getLocationBySlug(basePath(location.pathname));

  if (!loc) return <NotFound />;

  const displayLoc = getLocalizedLocation(loc);
  const city = displayLoc.cityLabel;

  const waText = buildTripMessage({
    pickup_city: loc.city,
    destination: "",
    message: `Enquiry from the ${loc.city} page`,
  });

  // Localize schema content while retaining the raw city, slug and provider identity.
  const jsonLd = buildLocationSchemas(locale === "hi" ? displayLoc : loc).map((schema) => (
    locale === "hi" && schema["@type"] === "FAQPage"
      ? {
        ...schema,
        mainEntity: [...displayLoc.faqs, ...getLocalBookingFaqsHi(city, displayLoc)].map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: { "@type": "Answer", text: faq.a },
        })),
      }
      : schema
  ));

  return (
    <>
      <Seo title={displayLoc.title} description={displayLoc.description} image={loc.heroImage} jsonLd={jsonLd} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#071426]">
        <div className="absolute inset-0"><OptimizedImage src={loc.heroImage} sizes="100vw" fetchPriority="high" alt={displayLoc.heroAlt} className="h-full w-full object-cover opacity-30" /><div className="absolute inset-0 bg-gradient-to-b from-[#071426]/80 to-[#071426]" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14 lg:pt-14 lg:pb-20">
          <Breadcrumbs items={[{ label: t("Home"), to: "/" }, { label: t("Service Areas"), to: "/service-areas" }, { label: city }]} />
          <a href="#travel-services" className="mt-5 inline-block text-sm font-semibold text-amber-300 underline underline-offset-4 hover:text-amber-200">{locale === "hi" ? "यात्रा सेवाओं और वाहन विकल्पों की तुलना करें" : "Compare travel services and vehicle options"}</a>
          <Eyebrow className="mt-5 text-amber-400 capitalize">{t(`${loc.priority} service area`)}</Eyebrow>
          <h1 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white">{displayLoc.h1}</h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-slate-300">{displayLoc.intro}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <QuoteButton prefill={{ pickup_city: loc.city }} testId="location-hero-quote-button">{t("Request a Quote")}</QuoteButton>
            <WhatsAppButton className="bg-emerald-600 px-5 py-3 text-white hover:bg-emerald-700" testId="location-hero-whatsapp-button" text={waText} label={t("WhatsApp Enquiry")} />
            <CallButton className="border border-slate-600 px-5 py-3 text-slate-100 hover:bg-white/5" testId="location-hero-call-button" label={t("Call Now")} />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
        <div className="grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-10">
            <div>
              <h2 className="text-2xl font-extrabold text-slate-900">{t("Car Hire, Car Rental & Cab Booking in {city}", { city })}</h2>
              <p className="mt-3 text-base leading-relaxed text-slate-600">{displayLoc.intro2}</p>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold text-slate-900">{t("What we help with in {city}", { city })}</h2>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                {displayLoc.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700 shadow-sm">
                    <Check className="mt-0.5 h-4 w-4 text-emerald-600 shrink-0" aria-hidden="true" />{h}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-xl bg-slate-50 border border-slate-200 p-5">
                <h3 className="flex items-center gap-2 font-bold text-slate-900"><Car className="h-5 w-5 text-amber-600" /> {t("Service types")}</h3>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {displayLoc.serviceTypes.map((s) => <li key={s} className="rounded-full bg-white border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600">{s}</li>)}
                </ul>
            </div>

            <VehicleOptions city={loc.displayName || loc.city} localGuidance={t(getVehicleGuidance(loc.slug))} compact />

            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="flex items-center gap-2 font-bold text-slate-900"><MapPin className="h-5 w-5 text-amber-600" /> {t("Pickup & how it's arranged")}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{displayLoc.pickupInfo}</p>
              <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
                <p>{displayLoc.dispatch}</p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold text-slate-900">{t("Quotation & conditions")}</h2>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{tParts("{tariff_emphasis} Local hire covers 120 KM / 12 hours plus fuel; outstation rates include fuel with a minimum of 300 KM per day and garage-to-garage billing. Extra-hour charges, applicable night charges, 5% GST and listed trip expenses are additional. Confirm the full itinerary, availability and final quotation before booking.", { tariff_emphasis: <strong key="tariff-emphasis">{t("Compare the published tariffs and request your trip quote.")}</strong> })}</p>
            </div>

            <div>
              <h2 className="text-2xl font-extrabold text-slate-900">{t("{city} travel FAQs", { city })}</h2>
              <div className="mt-4"><FaqAccordion faqs={displayLoc.faqs} /></div>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="space-y-6">
            <div className="rounded-2xl bg-[#0B1E36] p-6 text-white lg:sticky lg:top-20">
              <h3 className="text-lg font-bold">{t("Enquire about {city}", { city })}</h3>
              <p className="mt-2 text-sm text-slate-300">{t("Get a quick quote by WhatsApp or request a callback.")}</p>
              <div className="mt-4 flex flex-col gap-3">
                <QuoteButton prefill={{ pickup_city: loc.city }} className="w-full" testId="location-sidebar-quote-button">{t("Request a Quote")}</QuoteButton>
                <WhatsAppButton className="w-full bg-emerald-600 px-4 py-3 text-white hover:bg-emerald-700" testId="location-sidebar-whatsapp-button" text={waText} />
                <CallButton className="w-full bg-sky-600 px-4 py-3 text-white hover:bg-sky-700" testId="location-sidebar-call-button" label={t("Call {phone}", { phone: SITE.phonePrimary.display })} />
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="text-sm font-bold uppercase tracking-wider text-amber-600">{t("Connected routes")}</h3>
              <ul className="mt-4 space-y-2">
                {displayLoc.connections.map((c, index) => (
                  <li key={c.to}>
                    <Link to={canonicalPath(c.to)} className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:text-amber-700" data-testid={`location-connection-${loc.connections[index].label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                      {c.label} <ArrowRight className="h-4 w-4" />
                    </Link>
                  </li>
                ))}
                <li><Link to="/service-areas/" className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-50" data-testid="location-all-areas-link">{t("All service areas")} <ArrowRight className="h-4 w-4" /></Link></li>
              </ul>
            </div>
          </aside>
        </div>
        <ServiceBookingOptions location={loc} city={loc.city} className="mt-12" />
        <details className="mt-12 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="location-full-tariffs">
          <summary className="cursor-pointer text-xl font-bold text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">
            {t("Compare local, outstation, extra-hour & night charges for {city}", { city })}
          </summary>
          <div className="mt-6"><VehicleTariffTable pickupCity={loc.city} /></div>
          <div className="mt-6"><FareTerms compact /></div>
        </details>
      </section>
    </>
  );
}
